import {
    keepPreviousData,
    useMutation,
    useQueries,
    useQuery,
    UseQueryResult,
} from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { invoiceTemplateContract } from "@common/contracts/invoice-template.contract";
import {
    CreateInvoiceTemplateBody,
    INVOICE_TEMPLATE_LIST_MAX_LIMIT,
    InvoiceTemplateContentBody,
    InvoiceTemplateDetail,
    InvoiceTemplateListItem,
} from "@common/schemas/domains/invoice/invoice-template.schema";
import {
    InvoiceTemplateArchivedFilter,
    InvoiceTemplateSort,
} from "@common/types/invoiceTemplate";
import { Entity } from "@common/types/entity";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useEntitiesById } from "@hooks/useEntities";
import { useScope } from "@state/scopeContext.lib";
import { queryClient } from "../queryClient";

const invoiceTemplateContractClient = getBackendAPIClient(
    invoiceTemplateContract,
);

function suppressErrorToastHeaders(suppressErrorToast: boolean | undefined) {
    return suppressErrorToast
        ? { extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" } }
        : {};
}

const EMPTY_TEMPLATES: InvoiceTemplateListItem[] = [];

export function getInvoiceTemplatesQueryKey(
    workspaceId: string | null | undefined,
    entityId: number | undefined,
    params: {
        search?: string;
        archivedFilter?: InvoiceTemplateArchivedFilter;
        counterpartiesIds?: string[];
        withEmptyOrUnverifiedCounterparty?: boolean;
        hasCounterparty?: boolean;
        createdByUserIds?: number[];
        catalogItemIds?: string[];
        sort?: InvoiceTemplateSort;
        page?: number;
        limit?: number;
    } = {},
) {
    return ["invoiceTemplates", workspaceId, entityId, params] as const;
}

export function invalidateInvoiceTemplateQueries(workspaceId: string) {
    queryClient.invalidateQueries({
        queryKey: ["invoiceTemplates", workspaceId],
    });
    // Single-template details live under their own key, so the list
    // invalidation above does not reach them. Without this, reopening a
    // template right after saving hydrates the drawer from the pre-save cache
    // and the next save writes that stale content back over the edit.
    queryClient.invalidateQueries({
        queryKey: ["invoiceTemplate", workspaceId],
    });
}

interface InvoiceTemplatesQueryParams {
    search?: string;
    archivedFilter?: InvoiceTemplateArchivedFilter;
    counterpartiesIds?: string[];
    withEmptyOrUnverifiedCounterparty?: boolean;
    hasCounterparty?: boolean;
    createdByUserIds?: number[];
    catalogItemIds?: string[];
    sort?: InvoiceTemplateSort;
}

function getInvoiceTemplatesQueryOptions({
    workspaceId,
    entityId,
    enabled,
    ...params
}: InvoiceTemplatesQueryParams & {
    workspaceId: string | null | undefined;
    entityId: number | undefined;
    enabled: boolean;
}) {
    return {
        queryKey: getInvoiceTemplatesQueryKey(workspaceId, entityId, params),
        queryFn: async (): Promise<InvoiceTemplateListItem[]> => {
            if (!workspaceId || entityId === undefined) {
                return EMPTY_TEMPLATES;
            }
            const result =
                await invoiceTemplateContractClient.listInvoiceTemplates({
                    params: { workspaceId, entityId },
                    query: params,
                });
            return result.body.data;
        },
        enabled: enabled && Boolean(workspaceId) && entityId !== undefined,
        staleTime: 1000 * 60,
    };
}

export function useInvoiceTemplates({
    entityId,
    enabled = true,
    ...params
}: InvoiceTemplatesQueryParams & {
    entityId: number | undefined;
    enabled?: boolean;
}) {
    const { workspaceId } = useScope();

    return useQuery(
        getInvoiceTemplatesQueryOptions({
            workspaceId,
            entityId,
            enabled,
            ...params,
        }),
    );
}

const EMPTY_ENTITY_IDS: number[] = [];

function combineAllInvoiceTemplates(
    results: Array<UseQueryResult<InvoiceTemplateListItem[]>>,
) {
    return {
        templates: results.flatMap((result) => result.data ?? EMPTY_TEMPLATES),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        retry: () => {
            results
                .filter((result) => result.isError)
                .forEach((result) => result.refetch());
        },
    };
}

export function useAllInvoiceTemplatesForEntities({
    entityIds = EMPTY_ENTITY_IDS,
    enabled = true,
}: {
    entityIds?: number[];
    enabled?: boolean;
}) {
    const { workspaceId } = useScope();
    return useQueries({
        queries: entityIds.map((entityId) =>
            getInvoiceTemplatesQueryOptions({
                workspaceId,
                entityId,
                enabled,
                archivedFilter: InvoiceTemplateArchivedFilter.ALL,
            }),
        ),
        combine: combineAllInvoiceTemplates,
    });
}

interface EntityTemplatesPage {
    // Stamped so a placeholder page retained from a previous scope (a different
    // workspace or a reordered entity slot) can be dropped before render — see
    // the scope filter in useInvoiceTemplatesForEntities.
    entityId: number;
    data: InvoiceTemplateListItem[];
    total: number;
}

// Server-side pagination fans out per entity (each entity is its own scoped
// list endpoint — see listInvoiceTemplates), so a single logical "page N"
// across multiple entities can't be produced by one backend call. Instead,
// each entity is asked for its first `page * limit` sorted+filtered rows
// (a real bounded LIMIT/OFFSET query, not an unbounded fetch-everything), the
// per-entity pages are merged client-side with the same comparator the
// backend uses, and the merged list is sliced to the exact page window.
// `total` is summed from each entity's exact COUNT (independent of the
// requested depth), so pageCount stays accurate. This keeps every entity's
// query depth bounded and shrinks (rather than growing) whenever a filter/
// sort narrows the matching set, unlike the previous fully-unpaginated fetch.
//
// Two guards keep the fan-out sound:
//   - The per-entity depth is capped at INVOICE_TEMPLATE_LIST_MAX_LIMIT so a
//     deep page never asks the backend for a `limit` it rejects with a 400.
//     Because a correct client-merge of page N needs each entity's first
//     `page * limit` rows, that cap also bounds how deep the merge stays
//     exact, so pageCount is clamped to the same reachable window.
//   - Retained placeholder pages (keepPreviousData) from a previous scope are
//     dropped by entityId before merge/total, so switching workspace or entity
//     never briefly renders the prior scope's templates.
export function useInvoiceTemplatesForEntities({
    entityIds,
    search,
    archivedFilter,
    counterpartiesIds,
    withEmptyOrUnverifiedCounterparty,
    hasCounterparty,
    createdByUserIds,
    catalogItemIds,
    sort,
    page,
    limit,
    enabled = true,
}: {
    entityIds: number[];
    search?: string;
    archivedFilter?: InvoiceTemplateArchivedFilter;
    counterpartiesIds?: string[];
    withEmptyOrUnverifiedCounterparty?: boolean;
    hasCounterparty?: boolean;
    createdByUserIds?: number[];
    catalogItemIds?: string[];
    sort?: InvoiceTemplateSort;
    page: number;
    limit: number;
    enabled?: boolean;
}) {
    const { workspaceId } = useScope();
    const entitiesById = useEntitiesById();
    // Cap the per-entity fetch depth at the backend's max `limit` so a deep
    // page never triggers a 400. The merge is only guaranteed exact within
    // this depth, which is why pageCount is clamped to the matching window.
    const depth = Math.min(page * limit, INVOICE_TEMPLATE_LIST_MAX_LIMIT);
    const queries = useQueries({
        queries: entityIds.map((entityId) => ({
            queryKey: getInvoiceTemplatesQueryKey(workspaceId, entityId, {
                search,
                archivedFilter,
                counterpartiesIds,
                withEmptyOrUnverifiedCounterparty,
                hasCounterparty,
                createdByUserIds,
                catalogItemIds,
                sort,
                page: 1,
                limit: depth,
            }),
            queryFn: async (): Promise<EntityTemplatesPage> => {
                if (!workspaceId) {
                    return { entityId, data: EMPTY_TEMPLATES, total: 0 };
                }
                const result =
                    await invoiceTemplateContractClient.listInvoiceTemplates({
                        params: { workspaceId, entityId },
                        query: {
                            search,
                            archivedFilter,
                            counterpartiesIds,
                            withEmptyOrUnverifiedCounterparty,
                            hasCounterparty,
                            createdByUserIds,
                            catalogItemIds,
                            sort,
                            page: 1,
                            limit: depth,
                        },
                    });
                return {
                    entityId,
                    data: result.body.data,
                    total: result.body.total,
                };
            },
            enabled: enabled && Boolean(workspaceId),
            staleTime: 1000 * 60,
            // Keep the previous page rendered while the next page loads instead
            // of flashing an empty table and dropping the pagination controls,
            // matching other paginated lists (catalogItem.api.ts).
            placeholderData: keepPreviousData,
        })),
    });

    // keepPreviousData retains the prior query's data across any queryKey
    // change — including a workspace/entity switch — so drop any placeholder
    // page whose entity is no longer in scope before it can be rendered.
    const scopedEntityIds = new Set(entityIds);
    const scopedPages = queries
        .map((query) => query.data)
        .filter(
            (entityPage): entityPage is EntityTemplatesPage =>
                entityPage !== undefined &&
                scopedEntityIds.has(entityPage.entityId),
        );

    // Client-merges each per-entity query's already-backend-sorted+bounded
    // page using the same comparator as the active sort, so the combined
    // multi-entity list stays consistent with the requested order instead of
    // always falling back to updatedAt (the previous hardcoded merge key).
    const merged = scopedPages
        .flatMap((entityPage) => entityPage.data)
        .toSorted(compareInvoiceTemplatesBySort(sort, entitiesById));
    const total = scopedPages.reduce(
        (sum, entityPage) => sum + entityPage.total,
        0,
    );
    // Pages beyond the capped depth can't be merged exactly, so never expose
    // more pages than the fetched depth can serve.
    const maxReachablePages = Math.max(
        1,
        Math.floor(INVOICE_TEMPLATE_LIST_MAX_LIMIT / limit),
    );
    const pageCount = Math.min(
        Math.max(1, Math.ceil(total / limit)),
        maxReachablePages,
    );
    const start = (page - 1) * limit;
    const templates = merged.slice(start, start + limit);

    return {
        templates,
        total,
        pageCount,
        isLoading: queries.some((query) => query.isLoading),
        isFetching: queries.some((query) => query.isFetching),
    };
}

// Mirrors resolveTemplateOrder on the backend (invoice-template-query.internal
// .service.ts) so merging multiple already-sorted per-entity pages preserves
// the same order the backend would produce for a single entity. archivedAt is
// nullable, so it sorts NULLS LAST in both directions, matching the backend.
function compareInvoiceTemplatesBySort(
    sort: InvoiceTemplateSort | undefined,
    entitiesById: Map<number, Entity>,
) {
    const entityName = (template: InvoiceTemplateListItem) =>
        entitiesById.get(template.entityId)?.name ?? "";
    return (
        left: InvoiceTemplateListItem,
        right: InvoiceTemplateListItem,
    ): number => {
        switch (sort) {
            case InvoiceTemplateSort.NAME_ASC:
                return compareText(left.name, right.name);
            case InvoiceTemplateSort.NAME_DESC:
                return compareText(right.name, left.name);
            case InvoiceTemplateSort.CUSTOMER_ASC:
                return compareNullable(
                    left.customerName,
                    right.customerName,
                    compareText,
                );
            case InvoiceTemplateSort.CUSTOMER_DESC:
                return compareNullable(
                    left.customerName,
                    right.customerName,
                    (a, b) => compareText(b, a),
                );
            case InvoiceTemplateSort.ENTITY_ASC:
                return (
                    compareText(entityName(left), entityName(right)) ||
                    compareText(left.name, right.name)
                );
            case InvoiceTemplateSort.ENTITY_DESC:
                return (
                    compareText(entityName(right), entityName(left)) ||
                    compareText(left.name, right.name)
                );
            case InvoiceTemplateSort.TOTAL_ASC:
                return compareNullable(
                    left.total,
                    right.total,
                    (a, b) => a - b,
                );
            case InvoiceTemplateSort.TOTAL_DESC:
                return compareNullable(
                    left.total,
                    right.total,
                    (a, b) => b - a,
                );
            case InvoiceTemplateSort.INVOICES_ASC:
                return (
                    left.sourcedInvoiceCount - right.sourcedInvoiceCount ||
                    compareIds(left.id, right.id)
                );
            case InvoiceTemplateSort.INVOICES_DESC:
                return (
                    right.sourcedInvoiceCount - left.sourcedInvoiceCount ||
                    compareIds(left.id, right.id)
                );
            case InvoiceTemplateSort.LAST_USED_AT_ASC:
                return (
                    compareNullableDatesAsc(
                        left.lastUsedAt,
                        right.lastUsedAt,
                    ) || compareIds(left.id, right.id)
                );
            case InvoiceTemplateSort.LAST_USED_AT_DESC:
                return (
                    compareNullableDatesDesc(
                        left.lastUsedAt,
                        right.lastUsedAt,
                    ) || compareIds(left.id, right.id)
                );
            case InvoiceTemplateSort.CREATED_AT_ASC:
                return compareDatesAsc(left.createdAt, right.createdAt);
            case InvoiceTemplateSort.CREATED_AT_DESC:
                return compareDatesAsc(right.createdAt, left.createdAt);
            case InvoiceTemplateSort.UPDATED_AT_ASC:
                return compareDatesAsc(left.updatedAt, right.updatedAt);
            case InvoiceTemplateSort.ARCHIVED_AT_ASC:
                return compareNullableDatesAsc(
                    left.archivedAt,
                    right.archivedAt,
                );
            case InvoiceTemplateSort.ARCHIVED_AT_DESC:
                return compareNullableDatesDesc(
                    left.archivedAt,
                    right.archivedAt,
                );
            case InvoiceTemplateSort.UPDATED_AT_DESC:
            default:
                return compareDatesAsc(right.updatedAt, left.updatedAt);
        }
    };
}

function compareText(left: string, right: string): number {
    return left.localeCompare(right, undefined, { sensitivity: "base" });
}

// Matches the backend's `template.id ASC` tiebreaker (uuid byte order), so
// ties on usage keep the same order at every fetch depth and pages don't
// repeat or skip templates.
function compareIds(left: string, right: string): number {
    if (left === right) {
        return 0;
    }
    return left < right ? -1 : 1;
}

// NULLS LAST in both directions, matching the backend: direction lives in
// `compare`, so nulls never flip to the top on DESC.
function compareNullable<T>(
    left: T | null,
    right: T | null,
    compare: (a: T, b: T) => number,
): number {
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return 1;
    }
    if (right == null) {
        return -1;
    }
    return compare(left, right);
}

type DateLike = Date | string;
type NullableDateLike = DateLike | null;

function toTimestamp(value: DateLike): number {
    return (typeof value === "string" ? parseISO(value) : value).getTime();
}

function compareDatesAsc(left: DateLike, right: DateLike): number {
    return toTimestamp(left) - toTimestamp(right);
}

// NULLS LAST ascending: nulls always sort to the bottom.
function compareNullableDatesAsc(
    left: NullableDateLike,
    right: NullableDateLike,
): number {
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return 1;
    }
    if (right == null) {
        return -1;
    }
    return compareDatesAsc(left, right);
}

// NULLS LAST descending: nulls still sort to the bottom (matches the
// backend's `nulls: "LAST"` on both ASC and DESC), so this is not a simple
// negation of compareNullableDatesAsc.
function compareNullableDatesDesc(
    left: NullableDateLike,
    right: NullableDateLike,
): number {
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return 1;
    }
    if (right == null) {
        return -1;
    }
    return new Date(right).getTime() - new Date(left).getTime();
}

function getInvoiceTemplateQueryKey(
    workspaceId: string | null | undefined,
    entityId: number | undefined,
    templateId: string | undefined,
): readonly [
    "invoiceTemplate",
    string | null | undefined,
    number | undefined,
    string | undefined,
] {
    return ["invoiceTemplate", workspaceId, entityId, templateId];
}

async function loadInvoiceTemplate({
    workspaceId,
    entityId,
    templateId,
}: {
    workspaceId: string;
    entityId: number;
    templateId: string;
}): Promise<InvoiceTemplateDetail> {
    const result = await invoiceTemplateContractClient.getInvoiceTemplate({
        params: { workspaceId, entityId, templateId },
    });
    return result.body;
}

export function fetchInvoiceTemplate({
    workspaceId,
    entityId,
    templateId,
}: {
    workspaceId: string;
    entityId: number;
    templateId: string;
}): Promise<InvoiceTemplateDetail> {
    return queryClient.fetchQuery({
        queryKey: getInvoiceTemplateQueryKey(workspaceId, entityId, templateId),
        queryFn: () =>
            loadInvoiceTemplate({ workspaceId, entityId, templateId }),
        staleTime: 0,
    });
}

export function useInvoiceTemplate({
    entityId,
    templateId,
    enabled = true,
}: {
    entityId: number | undefined;
    templateId: string | undefined;
    enabled?: boolean;
}) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: getInvoiceTemplateQueryKey(workspaceId, entityId, templateId),
        queryFn: async (): Promise<InvoiceTemplateDetail | null> => {
            if (!workspaceId || entityId === undefined || !templateId) {
                return null;
            }
            return await loadInvoiceTemplate({
                workspaceId,
                entityId,
                templateId,
            });
        },
        enabled:
            enabled &&
            Boolean(workspaceId) &&
            entityId !== undefined &&
            Boolean(templateId),
        staleTime: 0,
    });
}

export function useCreateInvoiceTemplate() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            body,
            suppressErrorToast,
        }: {
            entityId: number;
            body: CreateInvoiceTemplateBody;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.createInvoiceTemplate({
                    params: { workspaceId, entityId },
                    body,
                    ...suppressErrorToastHeaders(suppressErrorToast),
                });
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
            }
        },
    });
}

export function useCreateInvoiceTemplateFromInvoice() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            invoiceId,
            name,
            suppressErrorToast,
        }: {
            entityId: number;
            invoiceId: string;
            name: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.createInvoiceTemplateFromInvoice(
                    {
                        params: { workspaceId, entityId, invoiceId },
                        body: { name },
                        ...suppressErrorToastHeaders(suppressErrorToast),
                    },
                );
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
            }
        },
    });
}

export function useRenameInvoiceTemplate() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            templateId,
            name,
            suppressErrorToast,
        }: {
            entityId: number;
            templateId: string;
            name: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.renameInvoiceTemplate({
                    params: { workspaceId, entityId, templateId },
                    body: { name },
                    ...suppressErrorToastHeaders(suppressErrorToast),
                });
            return result.body;
        },
        onSuccess: (_result, { entityId, templateId }) => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
                queryClient.invalidateQueries({
                    queryKey: getInvoiceTemplateQueryKey(
                        workspaceId,
                        entityId,
                        templateId,
                    ),
                });
            }
        },
    });
}

export function useArchiveInvoiceTemplate() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            templateId,
            suppressErrorToast,
        }: {
            entityId: number;
            templateId: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.archiveInvoiceTemplate({
                    params: { workspaceId, entityId, templateId },
                    body: {},
                    ...suppressErrorToastHeaders(suppressErrorToast),
                });
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
            }
        },
    });
}

export function useUnarchiveInvoiceTemplate() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            templateId,
            suppressErrorToast,
        }: {
            entityId: number;
            templateId: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.unarchiveInvoiceTemplate({
                    params: { workspaceId, entityId, templateId },
                    body: {},
                    ...suppressErrorToastHeaders(suppressErrorToast),
                });
            return result.body;
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
            }
        },
    });
}

export function useDeleteInvoiceTemplate() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            templateId,
            suppressErrorToast,
        }: {
            entityId: number;
            templateId: string;
            suppressErrorToast?: boolean;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            await invoiceTemplateContractClient.deleteInvoiceTemplate({
                params: { workspaceId, entityId, templateId },
                ...suppressErrorToastHeaders(suppressErrorToast),
            });
        },
        onSuccess: () => {
            if (workspaceId) {
                invalidateInvoiceTemplateQueries(workspaceId);
            }
        },
    });
}

export function useUpdateInvoiceTemplateContent() {
    const { workspaceId } = useScope();
    return useMutation({
        mutationFn: async ({
            entityId,
            templateId,
            body,
        }: {
            entityId: number;
            templateId: string;
            body: InvoiceTemplateContentBody;
        }) => {
            if (!workspaceId) {
                throw new Error("Expecting active workspace");
            }
            const result =
                await invoiceTemplateContractClient.updateInvoiceTemplateContent(
                    {
                        params: { workspaceId, entityId, templateId },
                        body,
                    },
                );
            return result.body;
        },
        onSuccess: (detail, { entityId, templateId }) => {
            if (!workspaceId) {
                return;
            }
            // Seed the detail cache with what the server just persisted: a
            // reopen renders the cached copy first (staleTime 0 refetches in
            // the background), so without this the drawer would hydrate from
            // the pre-save content.
            queryClient.setQueryData(
                getInvoiceTemplateQueryKey(workspaceId, entityId, templateId),
                detail,
            );
            invalidateInvoiceTemplateQueries(workspaceId);
        },
    });
}
